import { useEffect, useState } from "react";
import api, { formatApiErrorDetail, money } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Pencil, Trash2, ArrowRight } from "lucide-react";

const stages = [
  { key: "new", label: "New", color: "border-t-accent" },
  { key: "contacted", label: "Contacted", color: "border-t-[#E9C46A]" },
  { key: "quoted", label: "Quoted", color: "border-t-secondary" },
  { key: "won", label: "Won", color: "border-t-primary" },
  { key: "lost", label: "Lost", color: "border-t-muted-foreground" },
];
const nextStage = { new: "contacted", contacted: "quoted", quoted: "won", won: "won", lost: "lost" };

const empty = {
  name: "", email: "", phone: "", address: "", source: "website",
  service_interest: "", status: "new", estimated_value: 0, notes: "",
};

export default function Leads() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/leads").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(empty); setEditing(null); setOpen(true); };
  const openEdit = (l) => { setForm({ ...empty, ...l }); setEditing(l.id); setOpen(true); };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const payloadOf = (f) => {
    const p = { ...f, estimated_value: Number(f.estimated_value) || 0 };
    delete p.id; delete p.created_at; return p;
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    try {
      if (editing) await api.put(`/leads/${editing}`, payloadOf(form));
      else await api.post("/leads", payloadOf(form));
      toast.success(editing ? "Lead updated" : "Lead added");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const advance = async (l) => {
    await api.put(`/leads/${l.id}`, payloadOf({ ...l, status: nextStage[l.status] }));
    load();
  };

  const remove = async (id) => { await api.delete(`/leads/${id}`); toast.success("Lead removed"); load(); };

  return (
    <div className="space-y-6" data-testid="leads-page">
      <div className="flex justify-between items-center">
        <p className="text-sm text-muted-foreground">Track prospects from first contact to won job.</p>
        <Button data-testid="add-lead-btn" onClick={openNew} className="bg-primary text-primary-foreground hover:bg-[#152e17] hover:text-white transition-colors duration-200">
          <Plus className="h-4 w-4 mr-1.5" /> Add Lead
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-4">
        {stages.map((s) => {
          const list = items.filter((l) => l.status === s.key);
          const total = list.reduce((a, b) => a + (b.estimated_value || 0), 0);
          return (
            <div key={s.key} data-testid={`lead-column-${s.key}`} className="min-w-0">
              <div className={`bg-card border border-border border-t-4 ${s.color} rounded-lg p-3 mb-3`}>
                <div className="flex justify-between items-center">
                  <span className="font-display font-bold text-sm">{s.label}</span>
                  <span className="text-xs text-muted-foreground tabular-nums">{list.length}</span>
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">{money(total)}</div>
              </div>
              <div className="space-y-2">
                {list.map((l) => (
                  <Card key={l.id} data-testid="lead-card" className="p-3 border-border rounded-md bg-card hover:shadow-md transition-shadow duration-200">
                    <div className="flex justify-between items-start gap-2">
                      <div className="min-w-0">
                        <div className="font-semibold text-sm truncate">{l.name}</div>
                        <div className="text-xs text-muted-foreground truncate">{l.service_interest || l.source}</div>
                      </div>
                      <span className="text-xs font-semibold tabular-nums shrink-0">{money(l.estimated_value)}</span>
                    </div>
                    <div className="flex items-center gap-1 mt-3">
                      {s.key !== "won" && s.key !== "lost" && (
                        <button data-testid="advance-lead-btn" onClick={() => advance(l)} className="text-[0.7rem] flex items-center gap-1 px-2 py-1 rounded bg-primary/10 text-primary hover:bg-primary/20 transition-colors duration-200">
                          Move <ArrowRight className="h-3 w-3" />
                        </button>
                      )}
                      <div className="ml-auto flex gap-0.5">
                        <button onClick={() => openEdit(l)} className="p-1.5 rounded hover:bg-muted transition-colors duration-200"><Pencil className="h-3.5 w-3.5 text-muted-foreground" /></button>
                        <button onClick={() => remove(l.id)} className="p-1.5 rounded hover:bg-destructive/10 transition-colors duration-200"><Trash2 className="h-3.5 w-3.5 text-destructive" /></button>
                      </div>
                    </div>
                  </Card>
                ))}
                {list.length === 0 && <div className="text-xs text-muted-foreground text-center py-6 border border-dashed border-border rounded-md">Empty</div>}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit Lead" : "New Lead"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="col-span-2 space-y-1.5"><Label>Name *</Label><Input data-testid="lead-name-input" value={form.name} onChange={set("name")} /></div>
            <div className="space-y-1.5"><Label>Email</Label><Input value={form.email} onChange={set("email")} /></div>
            <div className="space-y-1.5"><Label>Phone</Label><Input value={form.phone} onChange={set("phone")} /></div>
            <div className="col-span-2 space-y-1.5"><Label>Address</Label><Input value={form.address} onChange={set("address")} /></div>
            <div className="space-y-1.5"><Label>Service Interest</Label><Input value={form.service_interest} onChange={set("service_interest")} placeholder="e.g. Weekly mowing" /></div>
            <div className="space-y-1.5"><Label>Est. Value ($)</Label><Input type="number" value={form.estimated_value} onChange={set("estimated_value")} /></div>
            <div className="space-y-1.5">
              <Label>Source</Label>
              <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["website", "referral", "google", "social", "flyer", "other"].map((o) => <SelectItem key={o} value={o} className="capitalize">{o}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Stage</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{stages.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2 space-y-1.5"><Label>Notes</Label><Textarea value={form.notes} onChange={set("notes")} rows={2} /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="save-lead-btn" onClick={save} className="bg-primary text-primary-foreground hover:bg-[#152e17] hover:text-white transition-colors duration-200">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
